// Client-side router — sole owner of history.pushState and window.location reads.
// Components call navigate(path) and listen for app:navigate events.

import { store } from './store.js'

const ROUTES = [
  { pattern: /^\/login$/, component: 'page-login', auth: false },
  { pattern: /^\/onboarding$/, component: 'page-onboarding', auth: true },
  { pattern: /^\/$/, component: 'page-calendar', auth: true },
  { pattern: /^\/calendar\/(\d{4}-\d{2})$/, component: 'page-calendar', auth: true },
  { pattern: /^\/days\/(\d{4}-\d{2}-\d{2})$/, component: 'page-day', auth: true },
  { pattern: /^\/settings$/, component: 'page-settings', auth: true },
  { pattern: /^\/reports$/, component: 'page-reports', auth: true, admin: true }
]

function match(pathname) {
  for (const route of ROUTES) {
    const m = pathname.match(route.pattern)
    if (m) return { ...route, params: m.slice(1) }
  }
  return { component: 'page-not-found', auth: false, params: [] }
}

export function navigate(path) {
  history.pushState(null, '', path)
  window.dispatchEvent(new CustomEvent('app:navigate', { detail: { path } }))
}

// Re-render the current route without changing history (e.g. after auth loads).
export function rerender() {
  window.dispatchEvent(new CustomEvent('app:navigate'))
}

export function getRoute() {
  return match(window.location.pathname)
}

export function setupRouter(routerEl) {
  const render = () => {
    const state = store.get()
    if (!state.authReady) {
      routerEl.currentRoute = { component: 'app-loading', auth: false, params: [] }
      routerEl.requestUpdate?.()
      return
    }

    const route = match(window.location.pathname)

    // Unauthenticated users hitting a protected route go to login.
    if (route.auth && !state.user) {
      if (window.location.pathname !== '/login') navigate('/login')
      routerEl.currentRoute = match('/login')
      routerEl.requestUpdate?.()
      return
    }

    // Authenticated users who have not finished onboarding are pinned to it.
    if (state.user && !state.user.onboardingComplete && route.component !== 'page-onboarding') {
      if (window.location.pathname !== '/onboarding') navigate('/onboarding')
      routerEl.currentRoute = match('/onboarding')
      routerEl.requestUpdate?.()
      return
    }

    // Admin-only routes are off-limits to everyone else — send them home.
    if (route.admin && state.user && !state.user.isAdmin) {
      navigate('/')
      return
    }

    // Logged-in users have no reason to see the login page.
    if (route.component === 'page-login' && state.user) {
      navigate('/')
      return
    }

    routerEl.currentRoute = route
    routerEl.requestUpdate?.()
  }

  window.addEventListener('popstate', render)
  window.addEventListener('app:navigate', render)
  const unsubscribe = store.subscribe(render)

  render()

  return () => {
    window.removeEventListener('popstate', render)
    window.removeEventListener('app:navigate', render)
    unsubscribe()
  }
}
